import React from 'react';
import { useShallow } from 'zustand/react/shallow';

import { useRosettaStore } from '../../../Stores/useRosettaStore';
import { calculatePricingHelper } from '../utils/rosettaPricingHelpers';

const VolumePricingTableCard = () => {
  const {
    tradesData,
    selectedTrades,
    volumeDiscountTiers,
    manualDiscount,
    manualDiscountEnabled,
    selectedScopes,
    inflationPercentage,
    perTradeDistribution,
    projectsPerYear,
    sheetsPerProject
  } = useRosettaStore(
    useShallow(state => ({
      tradesData: state.tradesData,
      selectedTrades: state.selectedTrades,
      volumeDiscountTiers: state.volumeDiscountTiers,
      manualDiscount: state.manualDiscount,
      manualDiscountEnabled: state.manualDiscountEnabled,
      selectedScopes: state.selectedScopes,
      inflationPercentage: state.inflationPercentage,
      perTradeDistribution: state.perTradeDistribution,
      projectsPerYear: state.projectsPerYear,
      sheetsPerProject: state.sheetsPerProject
    }))
  );

  // Calculate total annual sheets
  const totalAnnualSheets = projectsPerYear * sheetsPerProject;

  const pricing = calculatePricingHelper({
    selectedTrades,
    tradesData,
    selectedScopes,
    inflationPercentage,
    perTradeDistribution,
    totalAnnualSheets,
    volumeDiscountTiers,
    manualDiscountEnabled,
    manualDiscount
  });

  // Calculate tiers outside of JSX
  const tiers = Object.entries(volumeDiscountTiers)
    .map(([sheetCount, discount]) => ({
      minSheets: parseInt(sheetCount),
      discount: discount,
      tierId: `tier_${sheetCount}`
    }))
    .sort((a, b) => a.minSheets - b.minSheets);

  const tableRows = tiers.map((tier, index) => {
    const sheets = tier.minSheets;
    if (sheets === 0) return null; // Skip 0 tier

    // Apply both volume discount and manual discount additively
    const volumeDiscount = tier.discount / 100; // Convert percentage to decimal
    const manualDiscountPercent = manualDiscountEnabled ? manualDiscount / 100 : 0;
    const totalDiscount = volumeDiscount + manualDiscountPercent;

    const discountedPrice = pricing.blendedPrice * (1 - totalDiscount);
    const annualCost = discountedPrice * sheets;
    const quarterlyCost = annualCost / 4;

    // Find the next tier to determine the upper bound
    const nextTier = tiers[index + 1];
    const upperBound = nextTier ? nextTier.minSheets : Infinity;

    const isActive = pricing.totalSheetsFromTrades >= tier.minSheets && pricing.totalSheetsFromTrades < upperBound;

    return (
      <tr key={tier.tierId} className={isActive ? 'rosetta-active-tier' : ''}>
        <td>{sheets.toLocaleString()}+</td>
        <td>${discountedPrice.toFixed(1)}</td>
        <td>${annualCost.toLocaleString('en-US', { maximumFractionDigits: 0 })}</td>
        <td>${quarterlyCost.toLocaleString('en-US', { maximumFractionDigits: 0 })}</td>
      </tr>
    );
  });

  return (
    <section className='rosetta-pricing-card'>
      <h2 className='rosetta-pricing-title'>Volume Based Pricing Tiers</h2>
      <div className='rosetta-table-wrapper mt-4'>
        <table className='rosetta-pricing-table'>
          <thead>
            <tr>
              <th>Annual Sheets</th>
              <th>Price/Sheet</th>
              <th>Annual Cost</th>
              <th>Quarterly</th>
            </tr>
          </thead>
          <tbody>{tableRows}</tbody>
        </table>
      </div>
    </section>
  );
};

export default VolumePricingTableCard;
